<template>
  <div class="modern-form">
    <div class="form-container">
      <div class="section-header">
        <h2 class="section-title">Dental</h2>
      </div>

      <div class="patient-header">
        {{ patient.lastName }}, {{ patient.firstName }} ({{ patient.birthDate }})
      </div>

      <v-form ref="form">
        <div class="form-section">
          <h3 class="section-subtitle">Dental Form</h3>
          
          <div class="form-grid">
            <div class="form-row">
              <div class="form-group">
                <v-text-field 
                  v-model="form.reasonForVisit" 
                  label="Reason for your visit today"
                  class="line-input custom-filled"
                  dense
                  hide-details="auto" />
              </div>
              
              <div class="form-group">
                <v-text-field 
                  v-model="form.areYouInPain" 
                  label="Are you in pain?"
                  class="line-input custom-filled"
                  dense
                  hide-details="auto" />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <v-text-field 
                  v-model="form.lastCleaningExamDate" 
                  label="Date of last cleaning and exam"
                  type="date"
                  class="line-input custom-filled"
                  dense
                  hide-details="auto" />
              </div>
              
              <div class="form-group">
                <v-text-field 
                  v-model="form.goals" 
                  label="What goals do you have for your smile that we can help with?"
                  class="line-input custom-filled"
                  dense
                  hide-details="auto" />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <div class="question-section">
                  <h4 class="question-title">How do you approach your dental care?</h4>
                  <div class="modern-toggle">
                    <button 
                      type="button"
                      class="toggle-option"
                      :class="{ active: form.dentalAppraoch === 'proactive' }"
                      @click="form.dentalAppraoch = 'proactive'">
                      Proactive
                    </button>
                    <button 
                      type="button"
                      class="toggle-option"
                      :class="{ active: form.dentalAppraoch === 'reactive' }"
                      @click="form.dentalAppraoch = 'reactive'">
                      Reactive
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-subtitle">Do you have any of the following?</h3>
          <div class="checkbox-grid">
            <v-checkbox 
              v-for="item in dentalHabits" 
              :key="item.key"
              v-model="item.selected"
              :label="item.label"
              class="checkbox-item" />
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-subtitle">Previous Dental History?</h3>
          <div class="checkbox-grid">
            <v-checkbox 
              v-for="item in dentalHistory" 
              :key="item.key"
              v-model="item.selected"
              :label="item.label"
              class="checkbox-item" />
          </div>
        </div>
      </v-form>

      <Disclaimer ref="disclaimer"/>
    </div>
  </div>
</template>

<script>
import { jsPDF } from 'jspdf';
import DataService from '../../../_services/DataService';
import ValidateService from '../../../_services/ValidateService';
import emitEvent from '../../mixins/emitEvent';
import Disclaimer from './Disclaimer.vue';

export default {
  name: 'DentalForm',
  props: ['patient', 'needsUpdate'],
  mixins: [emitEvent],
  components: {
    Disclaimer,
  },
  data() {
    return {
      dentalHabits: [],
      dentalHistory: [],
      form: {
        id: 4,
        lastName: '',
        firstName: '',
        dateOfBirth: '',
        reasonForVisit: '',
        areYouInPain: '',
        lastCleaningExamDate: '',
        goals: '',
        dentalAppraoch: 'proactive',
      },
      rules: ValidateService.validators,
    };
  },
  watch: {
    async patient() {
      await this.getData();
    },
  },
  methods: {
    async validate() {
      return this.$refs.disclaimer.validate();
    },
    async generatePDF() {
      let y = 20;
      const doc = new jsPDF();
      doc.setFontSize(10).setFont(undefined, 'bold').text(10, y, 'Dental').setFont(undefined, 'normal');
      doc.text(10, y+=5, `Reason for your visit today: ${this.form.reasonForVisit || ''}`);
      doc.text(10, y+=5, `Are you in pain?: ${this.form.areYouInPain || ''}`);
      doc.text(10, y+=5, `Date of last cleaning and exam: ${this.form.lastCleaningExamDate || ''}`);
      doc.text(10, y+=5, `Goals: ${this.form.goals || ''}`);
      doc.text(10, y+=5, `Dental Approach: ${this.form.dentalAppraoch || ''}`);
      doc.setFont(undefined, 'bold').text(10, y+=5, 'Dental Habits').setFont(undefined, 'normal');

      y = this.generateLinesForSection(doc, y, 'Habits', this.dentalHabits);
      y = this.generateLinesForSection(doc, y, 'Dental History', this.dentalHistory);

      doc.addPage();
      y = 20;

      doc.text(10, y+=5, `Relationship to Patient: ${this.$refs.disclaimer.relationshipToPatient}`);

      if (this.$refs.disclaimer.relationshipToPatient !== 'Self') {
        doc.text(10, y+=5, `Name if not Patient: ${this.$refs.disclaimer.nameIfNotPatient}`);
      }

      doc.text(10, y+=5, `Date Signed: ${new Date().toISOString()}`);

      doc.addImage(this.$refs.disclaimer.$refs.signaturePad._data.signatureData, 'JPEG', 10, y+=10, 100, 70, undefined, 'FAST');

      await this.sendForm(doc.output('datauristring').split(',')[1]);
    },
    async sendForm(base64PDF) {
      this.form.selectedHabits = this.getSelectedIds(this.dentalHabits);
      this.form.selectedHistory = this.getSelectedIds(this.dentalHistory);
      this.form.removedHabits = this.getUnselectedIds(this.dentalHabits);
      this.form.removedHistory = this.getUnselectedIds(this.dentalHistory);

      this.form.base64PDF = base64PDF;

      const response = await (this.needsUpdate
        ? DataService.updatePatRegistration(this.form, this.patient.id)
        : DataService.postNewPatRegistration(this.form, this.patient.id));
      if (response.status === 200) {
        if (this.needsUpdate) {
          this.emitEvent('reloadPatientForm');
        } else {
          this.emitEvent('goToNextForm');
        }
        this.$root.$emit('raise_alert', { color: 'success', text: 'Your entry has been recorded.' });
      } else {
        this.emitEvent('submitFailure');
        this.$root.$emit('raise_alert', { color: 'error', text: 'We were unable to save your dental info, please try again or contact our team if the issue persists' });
      }
    },
    generateLinesForSection(doc, yStart, sectionName, sectionItems) {
      let updatedY = yStart;

      if (updatedY > 285) {
        doc.addPage();
        updatedY = 20;
      }

      doc.setFont(undefined, 'bold').text(10, updatedY += 5, sectionName).setFont(undefined, 'normal');

      for (const item of sectionItems) {
        if (updatedY > 285) {
          doc.addPage();
          updatedY = 20;
        }
        
        doc.text(10, updatedY += 5, `- ${item.label}: ${item.selected ? 'Yes' : 'No'}`);
      }

      return updatedY;
    },
    getSelectedNames(array) {
      return array.filter(item => item.selected).map(item => item.label);
    },
    getUnselectedNames(array) {
      return array.filter(item => !item.selected).map(item => item.label);
    },
    getSelectedIds(array) {
      return array.filter(item => item.selected).map(item => item.id);
    },
    getUnselectedIds(array) {
      return array.filter(item => !item.selected).map(item => item.id);
    },
    async getData() {
      const [clientDiseaseCategoriesResponse, diseaseDefinitionsResponse, patientDiseasesResponse] = await Promise.all([
        DataService.getClientDiseaseCategories(),
        DataService.getDiseaseDefinitions(),
        DataService.getPatientDiseases(this.patient.id),
      ]);

      if (diseaseDefinitionsResponse.status !== 200 || clientDiseaseCategoriesResponse.status !== 200) {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: 'An error occurred while loading the clinic configuration for this form.',
        });

        return;
      }

      let previouslySelectedDiseases = [];
      if (patientDiseasesResponse.status === 200) {
        previouslySelectedDiseases = patientDiseasesResponse.data.map(obj => obj.diseaseId);
      }

      const habits = clientDiseaseCategoriesResponse.data.find((cat) => cat.category === 'Habits');
      const history = clientDiseaseCategoriesResponse.data.find((cat) => cat.category === 'History');
      const habitIds = habits?.openDentalDiseaseIds ?? [];
      const historyIds = history?.openDentalDiseaseIds ?? [];

      const selectedDiseaseSet = new Set(previouslySelectedDiseases.map((v) => String(v)));
      const diseases = diseaseDefinitionsResponse.data.map((item) => ({
        id: item.DiseaseDefNum,
        label: item.DiseaseName,
        selected: selectedDiseaseSet.has(String(item.DiseaseDefNum)),
      }));

      // place items in correct categories
      this.dentalHabits = diseases.filter((item) => habitIds.includes(item.id));
      this.dentalHistory = diseases.filter((item) => historyIds.includes(item.id));
    },
  },
  async mounted() {
    await this.getData();
  },
};
</script>
<style lang="scss" scoped>
@import '@/scss/variables.scss';

// Form-specific color variables
$patient-header-bg-start: #133b54;
$patient-header-bg-end: #1a365d;
$patient-header-text: white;
$section-subtitle-color: #374151;
$question-title-color: #374151;

.patient-header {
  background: linear-gradient(180deg, $patient-header-bg-start 0%, $patient-header-bg-end 100%);
  color: $patient-header-text;
  padding: 16px;
  border-radius: 8px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 24px;
}

.form-section {
  margin-bottom: 32px;
}

.section-subtitle {
  font-size: 16px;
  font-weight: 600;
  color: $section-subtitle-color;
  margin: 0 0 16px 0;
}

.question-section {
  .question-title {
    font-size: 14px;
    font-weight: 600;
    color: $question-title-color;
    margin: 0 0 12px 0;
  }
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 8px;
  
  .checkbox-item {
    margin: 0;
  }
}
</style>
